import {
  Direction,
  EngagementFilterType,
  MessageContactType,
  MessagingROIType,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { i18next } from '@frontend/i18n';
import { DataCardProps } from '../data-card';

/**
 * Shared messaging ROI label/config primitives.
 *
 * These were originally defined inside the analytics-communication feature. They are now
 * owned by analytics-shared so they can be reused by any feature that renders the messaging
 * ROI chart + table (communication analytics, marketing bulk text, etc.).
 */

// ── Display names per ROI type ──
export const roiTypeLabels: Record<MessagingROIType, string> = {
  [MessagingROIType.ROI_TYPE_UNSPECIFIED]: '',
  [MessagingROIType.ROI_TYPE_MANUAL_MESSAGES]: i18next.t('Manual Outgoing Messages', { ns: 'analytics' }),
  [MessagingROIType.ROI_TYPE_MISSED_CALL_TEXT]: i18next.t('Missed Call Text', { ns: 'analytics' }),
  [MessagingROIType.ROI_TYPE_APPOINTMENT_REMINDERS]: i18next.t('Appointment Reminders', { ns: 'analytics' }),
  [MessagingROIType.ROI_TYPE_RECALL_REMINDERS]: i18next.t('Recall Reminders', { ns: 'analytics' }),
  [MessagingROIType.ROI_TYPE_REACTIVATION_TEXT]: i18next.t('Reactivation Text', { ns: 'analytics' }),
  [MessagingROIType.ROI_TYPE_RESCHEDULE_TEXT]: i18next.t('Reschedule Text', { ns: 'analytics' }),
  [MessagingROIType.ROI_TYPE_BULK_TEXT]: i18next.t('Bulk Text', { ns: 'analytics' }),
};

// ── Info-tip descriptions per ROI type ──
export const roiTypeInfotips: Partial<Record<MessagingROIType, string>> = {
  [MessagingROIType.ROI_TYPE_UNSPECIFIED]: '',
  [MessagingROIType.ROI_TYPE_MANUAL_MESSAGES]: i18next.t(
    'Engagement and scheduling for messages sent manually by staff',
    { ns: 'analytics' }
  ),
  [MessagingROIType.ROI_TYPE_MISSED_CALL_TEXT]: i18next.t(
    'Engagement and scheduling metrics for missed call text messages',
    { ns: 'analytics' }
  ),
  [MessagingROIType.ROI_TYPE_APPOINTMENT_REMINDERS]: i18next.t(
    'Engagement and scheduling metrics for appointment reminder messages',
    { ns: 'analytics' }
  ),
  [MessagingROIType.ROI_TYPE_RECALL_REMINDERS]: i18next.t(
    'Engagement and scheduling metrics for recall reminder messages',
    { ns: 'analytics' }
  ),
  [MessagingROIType.ROI_TYPE_REACTIVATION_TEXT]: i18next.t(
    'Engagement and scheduling metrics for reactivation text messages',
    { ns: 'analytics' }
  ),
  [MessagingROIType.ROI_TYPE_RESCHEDULE_TEXT]: i18next.t(
    'Engagement and scheduling metrics for reschedule text messages',
    { ns: 'analytics' }
  ),
  [MessagingROIType.ROI_TYPE_BULK_TEXT]: i18next.t('Engagement and scheduling metrics for bulk text messages', {
    ns: 'analytics',
  }),
};

// ── Engaged / non-engaged chart series labels ──
export const messagingRoiLabelMap: Record<string, string> = {
  engagementCount: i18next.t('Engaged Messages', { ns: 'analytics' }),
  nonEngagementCount: i18next.t('Non-engaged Messages', { ns: 'analytics' }),
};

// ── Table cell label maps ──
export const smsDirectionLabelMap: Partial<Record<Direction, string>> = {
  [Direction.DIRECTION_INBOUND]: i18next.t('Incoming', { ns: 'analytics' }),
  [Direction.DIRECTION_OUTBOUND]: i18next.t('Outgoing', { ns: 'analytics' }),
};

export const messageContactTypeLabelMap: Partial<Record<MessageContactType, string>> = {
  [MessageContactType.MESSAGES_EXISTING_PATIENTS]: i18next.t('Existing Patient', { ns: 'analytics' }),
  [MessageContactType.MESSAGES_UNKNOWN_PATIENTS]: i18next.t('Unknown', { ns: 'analytics' }),
};

export const engagementFilterTypeLabelMap: Partial<Record<EngagementFilterType, string>> = {
  [EngagementFilterType.ENGAGED]: i18next.t('Yes', { ns: 'analytics' }),
  [EngagementFilterType.NOT_ENGAGED]: i18next.t('No', { ns: 'analytics' }),
};

/** Combined label map for messaging table cell renderers. */
export const messagingTableLabelMap: Partial<Record<Direction | MessageContactType | EngagementFilterType, string>> = {
  ...smsDirectionLabelMap,
  ...messageContactTypeLabelMap,
  ...engagementFilterTypeLabelMap,
};

// ── Chart stacked-series config ──
export const messagingRoiChartSeriesKeys = ['nonEngagementCount', 'engagementCount'] as const;

export type MessagingRoiSeriesKey = (typeof messagingRoiChartSeriesKeys)[number];

/** Maps a stacked bar series key to the engagement filter value it represents. */
export const seriesKeyToEngaged: Partial<Record<string, EngagementFilterType>> = {
  engagementCount: EngagementFilterType.ENGAGED,
  nonEngagementCount: EngagementFilterType.NOT_ENGAGED,
};

// ── Full ROI config consumed by the chart view ──
export interface MessagingRoiConfig {
  chartTitle: string;
  chartInfoTip: string;
  cardsData: DataCardProps[];
  hideConfigureSettingsMsg: boolean;
}
